import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Layers, Handshake, Code2, Building2, Wallet, ShieldCheck, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";
import { FaqBlock } from "../../components/b2c/Blocks";
import { api } from "../../lib/api";

const PageShell = ({ overline, title, subtitle, children, testId }) => (
  <div className="pt-32 pb-16 px-6 lg:px-10" data-testid={testId}>
    <div className="max-w-5xl mx-auto">
      <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">{overline}</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white max-w-2xl">{title}</h1>
      <p className="mt-5 text-slate-400 max-w-2xl leading-relaxed">{subtitle}</p>
      <div className="mt-16">{children}</div>
    </div>
  </div>
);

const CTA = ({ label = "Contact Sales", testId }) => (
  <Link to="/contact" data-testid={testId} className="inline-flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200 mt-12">
    {label} <ArrowRight className="w-4 h-4" />
  </Link>
);

const FeatureGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    {items.map((f) => (
      <div key={f.title} className="glass-dark rounded-sm p-7">
        <f.icon className="w-6 h-6 text-[#E8767A] mb-4" />
        <h3 className="font-heading font-bold text-white">{f.title}</h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.text}</p>
      </div>
    ))}
  </div>
);

export function About() {
  usePageMeta(
    "About Sellvago — Content + technology platform for travel resellers",
    "Sellvago is a travel content and selling platform that enables travel resellers to independently service travellers under their own brand name."
  );
  return (
    <PageShell
      testId="about-page"
      overline="About Sellvago"
      title="The selling platform behind independent travel resellers."
      subtitle="Sellvago is a travel content and selling platform that enables travel resellers to independently service travellers under their own brand name."
    >
      <div className="glass-dark rounded-sm p-8 mb-10" data-testid="about-canonical-block">
        <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">What Sellvago is</p>
        <p className="text-slate-200 leading-relaxed" data-speakable>
          Sellvago is a travel content and a selling platform that enables travel resellers to independently
          service travellers under their own brand. Rather than functioning as a booking or a ticketing agent,
          Sellvago gives travel resellers the tools, content, and technology infrastructure to run and grow their
          own online travel business.
        </p>
        <p className="text-slate-400 leading-relaxed mt-4 text-sm" data-speakable>
          Sellvago is built for travel resellers, travel agents, corporates and entrepreneurs who want to sell
          travel under their own brand rather than as a channel for someone else's marketplace.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {[
          { h: "Content, not just inventory", p: "A library of airline, route and campaign content that resellers plug into their own storefront — so they sell more than a search box." },
          { h: "Technology, not a channel", p: "A branded booking engine and platform stack. Resellers own the customer relationship; Sellvago runs invisibly underneath." },
          { h: "Growth, not just bookings", p: "Top-performing resellers upgrade to a full white-label solution and build an independent travel business on our platform." },
        ].map((x) => (
          <div key={x.h} className="glass-dark rounded-sm p-7">
            <h3 className="font-heading font-bold text-white">{x.h}</h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{x.p}</p>
          </div>
        ))}
      </div>
      <CTA testId="about-contact-cta" />
    </PageShell>
  );
}

export function Solutions() {
  usePageMeta("Solutions — Sellvago for Agents, Agencies, Affiliates & API Partners", "Explore Sellvago solutions: agent booking tools, white-label portals, affiliate programs and flight search APIs.");
  return (
    <PageShell
      testId="solutions-page"
      overline="Solutions"
      title="Four ways to plug into Sellvago."
      subtitle="Whether you sell to travellers on WhatsApp or run a portal with thousands of users, there's a Sellvago model built for your workflow."
    >
      <FeatureGrid
        items={[
          { icon: Building2, title: "For travel agents & SMEs", text: "A powerful agent console: multi-supplier search, private fares, instant issue, reissue and refunds — with wallet or credit-line payments." },
          { icon: Layers, title: "White-label portals", text: "Your brand, our engine. A complete consumer or corporate booking site launched under your domain in days." },
          { icon: Handshake, title: "Affiliate program", text: "Deep links, airline & route landing pages and clean attribution. Earn on every booking your audience makes." },
          { icon: Code2, title: "Flight API", text: "Search, price and ticket via REST APIs. Build your own experience on top of Sellvago's supplier mesh." },
        ]}
      />
      <CTA testId="solutions-contact-cta" />
    </PageShell>
  );
}

export function WhiteLabel() {
  usePageMeta("White-label Booking Portal — Sellvago", "Launch a branded flight booking portal powered by Sellvago: multi-supplier fares, instant ticketing, your margin rules.");
  return (
    <PageShell
      testId="whitelabel-page"
      overline="White-label Portal"
      title="Your brand on the storefront. Our engine in the back."
      subtitle="A fully hosted booking portal under your domain and identity. You set the markups; we handle search, ticketing, payments plumbing and support tooling."
    >
      <FeatureGrid
        items={[
          { icon: Layers, title: "Branded end-to-end", text: "Logo, colors, domain, email templates — travellers only ever see your brand." },
          { icon: Wallet, title: "Your margin rules", text: "Route-level, airline-level and cabin-level markup control with promo code support." },
          { icon: ShieldCheck, title: "Compliance handled", text: "PCI-aware payment flows and supplier compliance handled by the platform." },
          { icon: Code2, title: "Launch in days", text: "Standard portals go live within a week. Custom sections and campaign pages available." },
        ]}
      />
      <CTA label="Request a Portal Demo" testId="whitelabel-demo-cta" />
    </PageShell>
  );
}

export function Affiliate() {
  usePageMeta("Affiliate & Partner Program — Sellvago", "Monetize your travel audience with Sellvago's affiliate program: deep links, landing pages and transparent commissions.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", meta_profile_url: "", audience: "", notes: "" });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/affiliates/signup", form);
      setResult(data);
      toast.success(data.already_registered ? "You're already registered — here's your link" : "You're in! Your affiliate link is ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const landingUrl = result ? `${typeof window !== "undefined" ? window.location.origin : ""}${result.landing_url}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(landingUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <PageShell
      testId="affiliate-page"
      overline="Affiliate Program"
      title="Turn your audience into airfare revenue."
      subtitle="Content creators, community admins and deal pages: send traffic to conversion-optimized airline and route landing pages, and earn on every completed booking."
    >
      <FeatureGrid
        items={[
          { icon: Handshake, title: "Ready-made landing pages", text: "A branded page at sellvago.com/a/{your-code} with an embedded search box — no site needed." },
          { icon: Code2, title: "Deep links & tracking", text: "Attribution parameters preserved through search, handoff and booking events. See counts in your dashboard." },
          { icon: Wallet, title: "Mixed commission model", text: "Per-segment, per-booking, percent-of-ticket and on-page ad revenue share — configured per affiliate." },
          { icon: ShieldCheck, title: "Support that converts", text: "Your users get WhatsApp booking help in Urdu & English, so warm traffic doesn't leak away." },
        ]}
      />

      {!result ? (
        <div className="mt-16 glass-dark rounded-sm p-8 max-w-2xl" data-testid="affiliate-signup-form">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">Join now</p>
          <h2 className="font-heading text-2xl font-bold text-white mb-2">Get your affiliate link in 30 seconds</h2>
          <p className="text-sm text-slate-400 mb-6">
            Auto-approved on signup. Meta OAuth is coming — for now just drop your Facebook profile link so we can verify you're a real creator.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input required data-testid="affiliate-signup-name" value={form.name} onChange={setField("name")} placeholder="Your name" className="w-full bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8767A]" />
              <input required type="email" data-testid="affiliate-signup-email" value={form.email} onChange={setField("email")} placeholder="Email" className="w-full bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8767A]" />
              <input data-testid="affiliate-signup-phone" value={form.phone} onChange={setField("phone")} placeholder="WhatsApp / phone" className="w-full bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8767A]" />
              <input required data-testid="affiliate-signup-meta" value={form.meta_profile_url} onChange={setField("meta_profile_url")} placeholder="Facebook / Instagram profile URL" className="w-full bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8767A]" />
            </div>
            <input data-testid="affiliate-signup-audience" value={form.audience} onChange={setField("audience")} placeholder="Audience size and niche (e.g. 80k Instagram, PK travel)" className="w-full bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8767A]" />
            <button type="submit" disabled={submitting} data-testid="affiliate-signup-submit" className="w-full bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-50 text-white font-bold py-3 rounded-sm transition-colors duration-200">
              {submitting ? "Creating your link…" : "Get my affiliate link →"}
            </button>
          </form>
        </div>
      ) : (
        <div className="mt-16 bg-gradient-to-br from-[#00172D] to-[#970C10] rounded-sm p-8 max-w-2xl text-white" data-testid="affiliate-signup-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold">{result.already_registered ? "Welcome back" : "You're in"}</p>
          </div>
          <h2 className="font-heading text-2xl font-bold mb-4">Your affiliate link is live</h2>
          <div className="bg-black/30 rounded-sm p-4 flex items-center gap-3">
            <p className="flex-1 font-mono text-sm truncate" data-testid="affiliate-result-url">{landingUrl}</p>
            <button onClick={copy} data-testid="affiliate-result-copy" className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold px-3 py-2 rounded transition-colors duration-200">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to={result.landing_url} data-testid="affiliate-result-preview" className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold text-sm px-5 py-2.5 rounded transition-colors duration-200">
              Preview my landing page <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={result.stats_url} data-testid="affiliate-result-stats" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white font-bold text-sm px-5 py-2.5 rounded transition-colors duration-200">
              View my stats
            </Link>
          </div>
          <p className="mt-5 text-xs text-white/70">
            Bookmark the stats URL to check click, search and handoff counts. Commissions are reconciled monthly.
          </p>
        </div>
      )}
    </PageShell>
  );
}

// Playbook-mandated FAQs (verbatim wording — do not paraphrase).
// These must render first so AI answer engines cite them as the canonical Sellvago answers.
const PLAYBOOK_FAQS = [
  {
    q: "What does Sellvago actually provide?",
    a: "Content, technology, and infrastructure that let travel resellers independently service travellers under their own brand — not just access to book inventory.",
  },
  {
    q: "How is Sellvago different from a B2B OTA?",
    a: "A B2B OTA sells inventory under its own brand and terms. Sellvago provides the technology and content resellers need to sell under their own brand, with their own pricing and commercial policy.",
  },
  {
    q: "What is Sellvago's white-label solution?",
    a: "A version of the Sellvago platform that top resellers can run under their own brand and commercial policy, allowing them to build an independent B2B travel business powered by Sellvago's technology.",
  },
  {
    q: "Does Sellvago have a B2C affiliate program?",
    a: "Yes. Affiliates share a unique Sellvago booking link with a trackable affiliate ID and earn compensation on every paid booking generated through that link.",
  },
  {
    q: "What markets does Sellvago operate in?",
    a: "Sellvago serves travel resellers with airline ticketing and related content, with ongoing expansion of fare and pricing infrastructure (1A PCCs) across additional markets.",
  },
  {
    q: "How do I become a Sellvago reseller?",
    a: "Resellers can sign up directly through the Sellvago platform; top-performing users are also invited to upgrade to the white-label solution to build their own branded business.",
  },
];

const B2B_FAQS = [
  { q: "Who can register on Sellvago?", a: "Freelance travel agents, registered agencies, affiliates and companies wanting white-label or API access. Individual consumers should use our Farevault storefront instead." },
  { q: "How fast can a white-label portal go live?", a: "Standard portals typically launch within 5–7 working days including branding, payment configuration and markup rules." },
  { q: "What suppliers does Sellvago aggregate?", a: "A mix of GDS, NDC direct-connects and low-cost carrier APIs — 40+ fare sources covering 900+ airlines worldwide." },
  { q: "How do payments work for agencies?", a: "Prepaid wallet top-ups by default, with credit lines available for qualifying agencies after volume review." },
  { q: "Is there an API sandbox?", a: "Yes. API prospects get sandbox credentials with full search and booking simulation before going live." },
  { q: "How are affiliate commissions tracked?", a: "Every affiliate link carries attribution parameters tracked through search, handoff and booking events, visible in your dashboard." },
];

export function Faq() {
  usePageMeta(
    "FAQs — Sellvago for travel resellers, agents and affiliates",
    "Frequently asked questions about Sellvago's content and technology platform for travel resellers — what we provide, how we differ from a B2B OTA, the white-label solution, affiliate program and markets."
  );

  useJsonLd("faq-page-breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://sellvago.com/" },
      { "@type": "ListItem", "position": 2, "name": "FAQs", "item": "https://sellvago.com/faq" },
    ],
  });

  useJsonLd("faq-page-schema", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["[data-speakable]"] },
    "mainEntity": [...PLAYBOOK_FAQS, ...B2B_FAQS].map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <PageShell
      testId="faq-page"
      overline="FAQs"
      title="Questions about Sellvago, answered."
      subtitle="Canonical answers to what Sellvago is, how it differs from a B2B OTA, and how travel resellers grow with the platform."
    >
      <div className="glass-dark rounded-sm p-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">About Sellvago</p>
        <FaqBlock faqs={PLAYBOOK_FAQS} testId="playbook-faq-accordion" light />
      </div>
      <div className="glass-dark rounded-sm p-8 mt-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">Partner operations</p>
        <FaqBlock faqs={B2B_FAQS} testId="b2b-faq-accordion" light />
      </div>
      <CTA testId="faq-contact-cta" />
    </PageShell>
  );
}

const Prose = ({ children }) => (
  <div className="prose prose-invert prose-slate max-w-3xl text-slate-300 leading-relaxed [&_h2]:text-white [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-white [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-sm [&_li]:text-sm [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-[#E8767A] [&_a]:underline">
    {children}
  </div>
);

export function Privacy() {
  usePageMeta(
    "Privacy Policy — Sellvago & Farevault",
    "How Sellvago and its consumer sub-brand Farevault collect, use and protect personal data. Last updated Feb 2026."
  );
  return (
    <PageShell
      testId="privacy-page"
      overline="Legal"
      title="Privacy Policy"
      subtitle="This policy explains what data Sellvago collects when you use sellvago.com or its consumer storefront Farevault, and how we protect it."
    >
      <Prose>
        <p className="text-xs text-slate-500">Effective date: 27 July 2026 · Last updated: 27 July 2026</p>

        <h2>1. Who we are</h2>
        <p>
          Sellvago is a Pakistan-based travel-technology platform. "Farevault" is Sellvago's consumer-facing sub-brand
          for discounted airline tickets. In this policy, "we", "us" and "Sellvago" refer to the same entity.
          Contact: <a href="mailto:support@sellvago.com">support@sellvago.com</a>.
        </p>

        <h2>2. What we collect</h2>
        <h3>a) Information you provide</h3>
        <ul>
          <li>Contact details when you submit a lead, contact form or agent signup (name, email, phone, company).</li>
          <li>Support messages you send us via email, form or WhatsApp.</li>
        </ul>
        <h3>b) Information we collect automatically</h3>
        <ul>
          <li>Pages viewed, links clicked and search parameters on sellvago.com (via Google Analytics 4 and our own analytics endpoint).</li>
          <li>Attribution parameters from your inbound URL (utm_source, utm_medium, utm_campaign, ref, aff, gclid, fbclid) to understand which campaigns work.</li>
          <li>Standard technical data (IP, user-agent, referrer) collected in server logs and rate-limit buckets.</li>
        </ul>
        <h3>c) Information we do not collect on sellvago.com</h3>
        <ul>
          <li>Payment card details. Booking payments happen off-site inside the Amadeus-powered booking engine at <code>sellvago.amadeusonlinesuite.com</code>.</li>
          <li>Passport, CNIC or visa data unless you send it to us voluntarily for booking assistance.</li>
        </ul>

        <h2>3. How we use it</h2>
        <ul>
          <li>Respond to your enquiries, provide booking assistance and issue tickets via our engine.</li>
          <li>Improve our airline and route pages based on aggregate search behaviour.</li>
          <li>Attribute conversions to marketing channels and affiliate partners.</li>
          <li>Detect and prevent abuse (rate-limiting, bot filtering).</li>
        </ul>

        <h2>4. Sharing</h2>
        <p>We share personal data only with:</p>
        <ul>
          <li>Airlines and GDS/aggregator suppliers, as required to search fares and issue your ticket.</li>
          <li>Payment processors on the booking engine, only for the payment step.</li>
          <li>Analytics providers (Google Analytics 4) with IP anonymisation enabled where supported.</li>
          <li>Authorities when legally compelled.</li>
        </ul>
        <p>We do not sell personal data.</p>

        <h2>5. Cookies & tracking</h2>
        <p>
          Sellvago.com uses first-party cookies and <code>sessionStorage</code> to remember your search preferences
          and attribution parameters. Google Analytics 4 sets its own cookies for anonymised usage measurement.
          You can clear these at any time from your browser settings.
        </p>

        <h2>6. AI crawlers and content usage</h2>
        <p>
          Sellvago explicitly permits AI search engines and assistants (GPTBot, ClaudeBot, PerplexityBot,
          Google-Extended and others listed in <a href="/robots.txt">robots.txt</a>) to index and cite public
          Sellvago pages. See <a href="/llms.txt">/llms.txt</a> for our AI-facing content policy.
        </p>

        <h2>7. Retention</h2>
        <p>
          Leads and support messages are retained for as long as they remain relevant to serving you and, after that,
          for the period required by tax and travel-industry compliance rules (typically 5 years). Analytics data is
          retained in aggregated form.
        </p>

        <h2>8. Your rights</h2>
        <p>
          You may request access to, correction of or deletion of your personal data by emailing
          <a href="mailto:support@sellvago.com"> support@sellvago.com</a>. We will respond within 30 days.
        </p>

        <h2>9. Changes</h2>
        <p>We update this policy when our practices change; the "Last updated" date at the top will reflect it.</p>
      </Prose>
    </PageShell>
  );
}

export function Terms() {
  usePageMeta(
    "Terms of Service — Sellvago & Farevault",
    "The terms that govern use of sellvago.com, its Farevault consumer storefront, and the Amadeus-powered booking engine."
  );
  return (
    <PageShell
      testId="terms-page"
      overline="Legal"
      title="Terms of Service"
      subtitle="By using sellvago.com or its Farevault consumer storefront, you agree to these terms."
    >
      <Prose>
        <p className="text-xs text-slate-500">Effective date: 27 July 2026 · Last updated: 27 July 2026</p>

        <h2>1. About these terms</h2>
        <p>
          These terms govern your access to and use of Sellvago's websites (sellvago.com and any subdomain),
          the Farevault consumer storefront at /flights, our public APIs and our booking assistance services.
          Bookings themselves are subject to the fare rules of the operating airline plus the terms of the
          Amadeus-powered booking engine at <code>sellvago.amadeusonlinesuite.com</code>.
        </p>

        <h2>2. Who can use Sellvago</h2>
        <ul>
          <li>You must be at least 18 years old (or the age of legal capacity where you live).</li>
          <li>Travel-agent accounts are approved after due-diligence checks.</li>
          <li>You agree to provide accurate information when submitting a lead, booking or agent signup.</li>
        </ul>

        <h2>3. Pricing and availability</h2>
        <p>
          Prices displayed on airline and route pages are indicative "starting from" fares drawn from our
          multi-supplier aggregator. Live price and availability are confirmed at the booking step inside
          the Amadeus-powered engine. Fares can change or sell out between listing and booking.
        </p>

        <h2>4. Bookings, changes and refunds</h2>
        <p>
          Tickets are issued by the operating airline. Refunds and changes follow that airline's fare rules,
          which we display before payment. Sellvago's support team can help you process changes for a service
          fee where applicable.
        </p>

        <h2>5. Multi-supplier aggregation disclaimer</h2>
        <p>
          Sellvago aggregates fares from multiple suppliers. The same flight may show different prices on the
          airline's own website; this is normal and reflects different negotiated fare classes. Sellvago is not
          the operating airline.
        </p>

        <h2>6. Acceptable use</h2>
        <ul>
          <li>Do not scrape, mirror or resell our content without a written agreement.</li>
          <li>Do not attempt to bypass rate limits or authentication on our APIs.</li>
          <li>Do not submit false leads, spam our contact forms or abuse our support channels.</li>
        </ul>

        <h2>7. AI-tool endpoints</h2>
        <p>
          Sellvago's <a href="/llms-full.txt">public LLM endpoints</a> (<code>/api/llms/offer</code>,
          <code>/api/llms/answer</code>, <code>/api/llms/facts.json</code>) are provided read-only for AI
          assistants and search engines under a citation-required policy. When quoting Sellvago content, cite
          the returned <code>book_url</code> or <code>citation_url</code>.
        </p>

        <h2>8. Intellectual property</h2>
        <p>
          Sellvago, Farevault and their logos are trademarks of Sellvago. Airline names and logos referenced on
          this site remain the trademarks of their respective owners; we use them for descriptive purposes only.
        </p>

        <h2>9. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, Sellvago is not liable for indirect or consequential losses
          arising from denied boarding, airline schedule changes, visa refusals or content-search errors that
          are the responsibility of the operating airline or supplier.
        </p>

        <h2>10. Governing law</h2>
        <p>
          These terms are governed by the laws of the Islamic Republic of Pakistan. Disputes are subject to the
          exclusive jurisdiction of the courts of Karachi, unless otherwise required by local consumer-protection law.
        </p>

        <h2>11. Contact</h2>
        <p>
          Questions about these terms: <a href="mailto:support@sellvago.com">support@sellvago.com</a>.
        </p>
      </Prose>
    </PageShell>
  );
}
